import React, { useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const CalculatorWrapper = styled.div`
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: 0.85rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: 0.02em;

  &::before {
    content: "";
    width: 3px;
    height: 14px;
    background: #0c4d96;
    border-radius: 2px;
  }
`;

const CalculatorContainer = styled.div`
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e8eaed;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const CalcHeader = styled.div`
  background: #0c4d96;
  padding: 0.9rem 1.15rem;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CalcHeaderIcon = styled.svg`
  width: 16px;
  height: 16px;
  stroke: white;
  fill: none;
`;

const CalcHeaderText = styled.span`
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
`;

const CalcBody = styled.div`
  padding: 1.15rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.15rem;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 0.65rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 9px 11px;
  border: 1px solid #e1e5e9;
  border-radius: 6px;
  font-size: 0.82rem;
  color: #111827;
  background: #ffffff;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #0c4d96;
    box-shadow: 0 0 0 3px rgba(12, 77, 150, 0.1);
  }
`;

const PositionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
`;

const PositionItem = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 9px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid ${(props) => (props.selected ? "#0c4d96" : "transparent")};
  background: ${(props) => (props.selected ? "#e8f2ff" : "#f8f9fa")};

  &:hover {
    background: ${(props) => (props.selected ? "#e8f2ff" : "#f0f2f5")};
  }
`;

const PositionCheckbox = styled.input`
  width: 13px;
  height: 13px;
  accent-color: #0c4d96;
  margin: 0;
  cursor: pointer;
  flex-shrink: 0;
`;

const PositionLabel = styled.span`
  font-size: 0.7rem;
  color: #374151;
  line-height: 1.25;
`;

const SalaryDisplay = styled.div`
  background: linear-gradient(135deg, #0c4d96 0%, #063a73 100%);
  border-radius: 8px;
  padding: 1.25rem 1rem;
  text-align: center;
  margin-top: 0.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50%;
    right: -30%;
    width: 80%;
    height: 150%;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.08) 0%,
      transparent 60%
    );
    pointer-events: none;
  }
`;

const SalaryLabel = styled.div`
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  position: relative;
  margin-bottom: 0.4rem;
`;

const SalaryValue = styled.div`
  font-size: 2.75rem;
  font-weight: 700;
  color: white;
  line-height: 1;
  position: relative;
  letter-spacing: -0.02em;
`;

const SalaryUnit = styled.span`
  font-size: 1rem;
  font-weight: 500;
  opacity: 0.95;
  margin-left: 4px;
  vertical-align: middle;
`;

const SalaryNote = styled.div`
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 0.6rem;
  position: relative;
`;

const Breakdown = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eef0f3;
`;

const BreakdownItem = styled.div`
  text-align: center;
  padding: 8px 6px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const BreakdownLabel = styled.div`
  font-size: 0.55rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
`;

const BreakdownValue = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${(props) => (props.accent ? "#059669" : "#111827")};
`;

// Data from Excel screenshot - matching the exact values
const experienceData = {
  "1-2": { bonus: 0, cumulative: 0, cap: 420 },
  "2-3": { bonus: 24, cumulative: 24, cap: 500 },
  "3-4": { bonus: 48, cumulative: 72, cap: 500 },
  "4-5": { bonus: 24, cumulative: 96, cap: 550 },
  "5-6": { bonus: 24, cumulative: 120, cap: 550 },
  "6-7": { bonus: 48, cumulative: 168, cap: 600 },
  "7-8": { bonus: 24, cumulative: 192, cap: 600 },
  "8-9": { bonus: 12, cumulative: 204, cap: 648 },
  "9-10": { bonus: 12, cumulative: 216, cap: 648 },
  "10+": { bonus: 24, cumulative: 240, cap: 690 },
};

const positionData = {
  pm: { base: 90, cap: 690 }, // Project Manager - プロジェクトマネージャー
  pl: { base: 90, cap: 690 }, // Project Leader - プロジェクトリーダー
  rd: { base: 84, cap: 636 }, // Requirements Definition - 要件定義
  bd: { base: 84, cap: 636 }, // Basic Design - 基本設計
  dd: { base: 72, cap: 624 }, // Detailed Design - 詳細設計
  impl: { base: 72, cap: 624 }, // Implementation - 実装/インフラ構築
  ops: { base: 36, cap: 438 }, // Operations - 運用保守
  test: { base: 0, cap: 432 }, // Testing - テスト
};

const BASE_SALARY = 336;

export default function SalaryCalculator() {
  const { t } = useTranslation();
  const [experience, setExperience] = useState("1-2");
  const [selectedPositions, setSelectedPositions] = useState(["dd"]);

  const handlePositionChange = useCallback((positionKey, checked) => {
    setSelectedPositions((prev) => {
      if (checked) {
        return [...prev, positionKey];
      } else {
        return prev.filter((p) => p !== positionKey);
      }
    });
  }, []);

  const calculation = useMemo(() => {
    let maxPositionBonus = 0;
    let maxPositionCap = 420;

    selectedPositions.forEach((position) => {
      if (positionData[position]) {
        if (positionData[position].base > maxPositionBonus) {
          maxPositionBonus = positionData[position].base;
        }
        if (positionData[position].cap > maxPositionCap) {
          maxPositionCap = positionData[position].cap;
        }
      }
    });

    // Default to Detailed Design if nothing selected
    if (selectedPositions.length === 0) {
      maxPositionBonus = 72;
      maxPositionCap = 624;
    }

    const expData = experienceData[experience];
    const expBonus = expData.cumulative;

    // Calculate: base + position bonus + experience bonus
    let salary = BASE_SALARY + maxPositionBonus + expBonus;

    // Apply cap - using the minimum of position cap and experience cap
    const effectiveCap = Math.min(maxPositionCap, expData.cap);
    salary = Math.min(salary, effectiveCap);

    return {
      salary,
      positionBonus: maxPositionBonus,
      expBonus,
      positionCap: maxPositionCap,
    };
  }, [experience, selectedPositions]);

  const positions = [
    { key: "pm", label: t("salary_calculator.positions.pm") },
    { key: "pl", label: t("salary_calculator.positions.pl") },
    { key: "rd", label: t("salary_calculator.positions.rd") },
    { key: "bd", label: t("salary_calculator.positions.bd") },
    { key: "dd", label: t("salary_calculator.positions.dd") },
    { key: "impl", label: t("salary_calculator.positions.impl") },
    { key: "ops", label: t("salary_calculator.positions.ops") },
    { key: "test", label: t("salary_calculator.positions.test") },
  ];

  const experienceOptions = [
    { value: "1-2", label: t("salary_calculator.experience.1-2") },
    { value: "2-3", label: t("salary_calculator.experience.2-3") },
    { value: "3-4", label: t("salary_calculator.experience.3-4") },
    { value: "4-5", label: t("salary_calculator.experience.4-5") },
    { value: "5-6", label: t("salary_calculator.experience.5-6") },
    { value: "6-7", label: t("salary_calculator.experience.6-7") },
    { value: "7-8", label: t("salary_calculator.experience.7-8") },
    { value: "8-9", label: t("salary_calculator.experience.8-9") },
    { value: "9-10", label: t("salary_calculator.experience.9-10") },
    { value: "10+", label: t("salary_calculator.experience.10+") },
  ];

  return (
    <CalculatorWrapper>
      <SectionTitle>{t("salary_calculator.title")}</SectionTitle>
      <CalculatorContainer>
        <CalcHeader>
          <CalcHeaderIcon viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round">
            <rect x="4" y="2" width="16" height="20" rx="2" />
            <line x1="8" y1="6" x2="16" y2="6" />
            <line x1="8" y1="10" x2="16" y2="10" />
            <line x1="8" y1="14" x2="10" y2="14" />
            <line x1="8" y1="18" x2="10" y2="18" />
          </CalcHeaderIcon>
          <CalcHeaderText>{t("salary_calculator.header")}</CalcHeaderText>
        </CalcHeader>
        <CalcBody>
          <FormGroup>
            <FormLabel>{t("salary_calculator.experience_label")}</FormLabel>
            <FormSelect
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
            >
              {experienceOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </FormSelect>
          </FormGroup>

          <FormGroup>
            <FormLabel>{t("salary_calculator.position_label")}</FormLabel>
            <PositionGrid>
              {positions.map((pos) => (
                <PositionItem
                  key={pos.key}
                  selected={selectedPositions.includes(pos.key)}
                >
                  <PositionCheckbox
                    type="checkbox"
                    checked={selectedPositions.includes(pos.key)}
                    onChange={(e) =>
                      handlePositionChange(pos.key, e.target.checked)
                    }
                  />
                  <PositionLabel>{pos.label}</PositionLabel>
                </PositionItem>
              ))}
            </PositionGrid>
          </FormGroup>

          <SalaryDisplay>
            <SalaryLabel>{t("salary_calculator.estimated_salary")}</SalaryLabel>
            <SalaryValue>
              {calculation.salary}
              <SalaryUnit>{t("salary_calculator.unit")}</SalaryUnit>
            </SalaryValue>
            <SalaryNote>{t("salary_calculator.note")}</SalaryNote>
          </SalaryDisplay>

          <Breakdown>
            <BreakdownItem>
              <BreakdownLabel>{t("salary_calculator.breakdown.position")}</BreakdownLabel>
              <BreakdownValue>+{calculation.positionBonus}</BreakdownValue>
            </BreakdownItem>
            <BreakdownItem>
              <BreakdownLabel>{t("salary_calculator.breakdown.experience")}</BreakdownLabel>
              <BreakdownValue accent>+{calculation.expBonus}</BreakdownValue>
            </BreakdownItem>
            <BreakdownItem>
              <BreakdownLabel>{t("salary_calculator.breakdown.cap")}</BreakdownLabel>
              <BreakdownValue>{calculation.positionCap}</BreakdownValue>
            </BreakdownItem>
          </Breakdown>
        </CalcBody>
      </CalculatorContainer>
    </CalculatorWrapper>
  );
}
